import '@apps/web/features/phoebe_chat_v3/ui/primitives/chat_motion.css';

import { type ReactNode, useId } from 'react';

import {
    type ApprovalPresentation,
    type NoteResult,
} from '@apps/web/features/phoebe_chat_v3/registry/types';
import { CompactReceipt } from '@apps/web/features/phoebe_chat_v3/ui/primitives/compact_receipt';
import { ReferenceText } from '@apps/web/features/phoebe_chat_v3/ui/primitives/reference_badge';

export type SaveNoteCardDecision = 'pending' | 'allowed' | 'denied';

type SaveNoteCardProps = Readonly<{
    readonly presentation: ApprovalPresentation;
    readonly decision: SaveNoteCardDecision;
    readonly outcome?: 'pending' | 'succeeded' | 'failed';
    readonly noteResult?: NoteResult;
    readonly onSave: () => void;
    readonly onCancel: () => void;
    readonly disabled?: boolean;
}>;

function SaveNoteIcon(): ReactNode {
    return (
        <svg
            viewBox="0 0 24 24"
            aria-hidden={true}
            className="phoebe-save-note-icon text-foreground size-[18px] shrink-0"
        >
            <rect
                x="4.5"
                y="3.5"
                width="13"
                height="17"
                rx="2.25"
                fill="currentColor"
                fillOpacity="0.04"
                stroke="currentColor"
                strokeOpacity="0.5"
                strokeWidth="1.25"
            />
            <path
                className="phoebe-save-note-line"
                d="M7.5 9.2c1.1-.5 2.1-.5 3.2 0s2.1.5 3.3-.1"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="1.35"
            />
            <path
                className="phoebe-save-note-line"
                d="M7.5 13.2c1.2-.5 2.2-.5 3.4 0s2.1.5 3.3-.1"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="1.35"
            />
            <g className="phoebe-save-note-pen">
                <path
                    d="m14.1 5.9 3.7 3.7m-5.5-1.9 1.8-1.8 3.7 3.7-1.8 1.8-3.7-3.7Z"
                    fill="currentColor"
                    fillOpacity="0.08"
                    stroke="currentColor"
                    strokeLinejoin="round"
                    strokeWidth="1.2"
                />
            </g>
        </svg>
    );
}

function NoteContent({
    presentation,
}: Pick<SaveNoteCardProps, 'presentation'>) {
    const groups = presentation.groups.filter(
        (group) =>
            group.items.length > 0 && group.heading.toLowerCase() !== 'reason',
    );
    return (
        <div className="text-v3-chat space-y-1">
            {groups.map((group, groupIndex) => (
                <p
                    key={`${group.heading}-${groupIndex}`}
                    className="text-muted-foreground m-0 whitespace-pre-wrap"
                >
                    <ReferenceText
                        text={
                            (groups.length > 1 ||
                            group.heading.toLowerCase() !== 'note'
                                ? `${group.heading}: `
                                : '') + group.items.join(' ')
                        }
                    />
                </p>
            ))}
        </div>
    );
}

export function SaveNoteCard({
    presentation,
    decision,
    outcome = 'pending',
    noteResult,
    onSave,
    onCancel,
    disabled = false,
}: SaveNoteCardProps): ReactNode {
    const titleId = useId();
    const pending = decision === 'pending';
    const incompleteNote =
        outcome === 'succeeded' &&
        noteResult !== undefined &&
        noteResult.status !== 'saved';
    const failed =
        decision === 'allowed' && (outcome === 'failed' || incompleteNote);
    const running = decision === 'allowed' && outcome === 'pending';
    if (!pending) {
        return (
            <CompactReceipt
                status={
                    decision === 'denied'
                        ? 'denied'
                        : running
                          ? 'running'
                          : failed
                            ? 'failed'
                            : 'succeeded'
                }
                title={
                    decision === 'denied'
                        ? 'Note not saved'
                        : running
                          ? 'Saving note'
                          : failed
                            ? noteResult?.status === 'partial'
                                ? 'Note partially saved'
                                : 'Couldn’t save note'
                            : 'Note saved'
                }
                summary={
                    decision === 'denied' || running || failed ? undefined : (
                        <NoteContent presentation={presentation} />
                    )
                }
            />
        );
    }
    return (
        <section
            aria-labelledby={titleId}
            className="bg-card text-foreground w-full min-w-0 rounded-lg border px-3 py-2.5 [overflow-wrap:anywhere]"
        >
            <div className="flex items-center gap-2">
                <SaveNoteIcon />
                <h3
                    id={titleId}
                    className="text-v3-chat m-0 min-w-0 flex-1 font-semibold"
                >
                    Do you want to save this note?
                </h3>
            </div>
            <div className="mt-2">
                <NoteContent presentation={presentation} />
            </div>
            <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
                <button
                    type="button"
                    disabled={disabled}
                    onClick={onSave}
                    className="bg-foreground text-background focus-visible:ring-ring text-v3-chat min-h-8 rounded-md px-3 font-semibold focus-visible:ring-2 disabled:opacity-40"
                >
                    Save
                </button>
                <button
                    type="button"
                    disabled={disabled}
                    onClick={onCancel}
                    className="text-muted-foreground focus-visible:ring-ring text-v3-chat min-h-8 rounded-md border px-3 font-semibold focus-visible:ring-2 disabled:opacity-40"
                >
                    Cancel
                </button>
            </div>
        </section>
    );
}
